import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Globe, Search, Plus, Minus, RotateCcw, Check, Package, TrendingUp, Clock, AlertCircle, Trash2, X, ShoppingCart, Upload, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Calendar, List, Filter, LayoutGrid, Table2, Download, Pencil, CalendarDays } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCHES = {
  "QC": "Quezon City",
  "PQ": "Parañaque",
  "CV": "Cavite",
  "PM": "Pampanga",
  "VZ": "Valenzuela",
  "OTG-A": "Orion On The Go - A",
  "OTG-B": "Orion On The Go - B",
  "OTG-C": "Orion On The Go - C"
};

const CHANNELS = ["Shopee", "TikTok", "J&T", "Lazada", "Facebook", "Other"];

export default function OnlineSales() {
  const { user, isAdmin } = useAuth();
  
  // Branch restriction logic - check both is_admin and can_access_all_branches  
  const canAccessAllBranches = user?.can_access_all_branches || user?.is_admin || user?.role === "admin" || false;
  const userBranches = user?.assigned_branches?.length ? user.assigned_branches : (user?.assigned_branch ? [user.assigned_branch] : []);
  const userBranch = userBranches[0] || null;
  
  // Users without branch AND not admin = VIEW ONLY
  const canEdit = canAccessAllBranches || !!userBranch;
  
  const [selectedBranch, setSelectedBranch] = useState(() => {
    if (!canAccessAllBranches && userBranch) {
      return userBranch;
    }
    return "QC";
  });
  
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // CART STATE - multiple items per order
  const [cart, setCart] = useState([]);
  const [channel, setChannel] = useState("Shopee");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  
  // Stock cache
  const [stockCache, setStockCache] = useState({});
  
  // Recent sales and stats
  const [recentSales, setRecentSales] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Delete modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Date filter for viewing/deleting sales
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  // Records view modal - with DATE RANGE
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [recordsStartDate, setRecordsStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [recordsEndDate, setRecordsEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [allRecords, setAllRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [recordsStats, setRecordsStats] = useState({ total: 0, totalQty: 0 });
  const [recordsViewMode, setRecordsViewMode] = useState('channel'); // 'channel' or 'table'
  
  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    quantity: 0,
    tracking_number: "",
    channel: "",
    sale_date: "",
    customer_name: "",
    address: "",
    notes: ""
  });
  const [editSaving, setEditSaving] = useState(false);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectGroup = (records) => {
    const ids = records.map(r => r.id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = ids.length > 0 && ids.every(id => next.has(id));
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDeleteSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error("Wala kang napiling records");
      return;
    }
    
    if (!window.confirm(`Sigurado ka bang i-delete ang ${selectedIds.size} online sales record(s)?\n\nLahat ng stocks ay BABALIK sa Display Area.`)) {
      return;
    }

    setBulkDeleting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(`${API}/online-sales/bulk-delete`, {
        sale_ids: Array.from(selectedIds)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.errors > 0) {
        toast.warning(`${response.data.deleted} deleted, ${response.data.errors} failed. Stock restored: +${response.data.total_qty_restored}`);
      } else {
        toast.success(response.data.message);
      }
      
      clearSelection();
      await fetchAllRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };
  
  // Channel colors and config
  const CHANNEL_CONFIG = {
    'J&T': { color: 'bg-red-500', textColor: 'text-white', bgLight: 'bg-red-50', borderColor: 'border-red-300' },
    'Shopee': { color: 'bg-orange-500', textColor: 'text-white', bgLight: 'bg-orange-50', borderColor: 'border-orange-300' },
    'TikTok': { color: 'bg-gray-900', textColor: 'text-white', bgLight: 'bg-gray-100', borderColor: 'border-gray-400' },
    'Lazada': { color: 'bg-blue-600', textColor: 'text-white', bgLight: 'bg-blue-50', borderColor: 'border-blue-300' },
    'LBC': { color: 'bg-yellow-500', textColor: 'text-black', bgLight: 'bg-yellow-50', borderColor: 'border-yellow-300' },
    'Flash Express': { color: 'bg-green-500', textColor: 'text-white', bgLight: 'bg-green-50', borderColor: 'border-green-300' },
    'Other': { color: 'bg-gray-500', textColor: 'text-white', bgLight: 'bg-gray-50', borderColor: 'border-gray-300' }
  };
  
  // Group records by channel
  const getRecordsByChannel = () => {
    const grouped = {};
    const channels = ['J&T', 'Shopee', 'TikTok', 'Lazada', 'LBC', 'Flash Express', 'Other'];
    
    // Initialize all channels
    channels.forEach(ch => {
      grouped[ch] = { records: [], totalQty: 0 };
    });
    
    // Group records
    allRecords.forEach(record => {
      let channel = record.channel || 'Other';
      // Normalize channel names
      if (channel.toLowerCase().includes('j&t') || channel.toLowerCase().includes('jnt') || channel.toLowerCase().includes('j & t')) {
        channel = 'J&T';
      } else if (channel.toLowerCase().includes('shopee')) {
        channel = 'Shopee';
      } else if (channel.toLowerCase().includes('tiktok') || channel.toLowerCase().includes('tik tok')) {
        channel = 'TikTok';
      } else if (channel.toLowerCase().includes('lazada')) {
        channel = 'Lazada';
      } else if (channel.toLowerCase().includes('lbc')) {
        channel = 'LBC';
      } else if (channel.toLowerCase().includes('flash')) {
        channel = 'Flash Express';
      } else if (!channels.includes(channel)) {
        channel = 'Other';
      }
      
      if (!grouped[channel]) {
        grouped[channel] = { records: [], totalQty: 0 };
      }
      grouped[channel].records.push(record);
      grouped[channel].totalQty += record.quantity || 0;
    });
    
    return grouped;
  };
  
  // CSV Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importStats, setImportStats] = useState(null);
  const [importChannel, setImportChannel] = useState("Shopee"); // Default channel for import
  const [importResults, setImportResults] = useState([]); // Results after import attempt
  const [importDate, setImportDate] = useState(new Date().toISOString().split('T')[0]); // Date for imported sales
  const fileInputRef = useRef(null);
  const searchRef = useRef(null);

  // Fetch item masterlist
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/item-master/list`);
      setItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching items:", error);
      toast.error("Failed to load items");
    }
  };

  // Fetch recent sales
  const fetchRecentSales = async () => {
    try {
      const response = await axios.get(`${API}/online-sales`, {
        params: { branch: selectedBranch, limit: 15 }
      });
      setRecentSales(response.data.sales || []);
    } catch (error) {
      console.error("Error fetching sales:", error);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/online-sales/stats`, {
        params: { branch: selectedBranch, days: 30 }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Fetch sales for a specific date (for delete all feature)
  const fetchSalesByDate = async (date) => {
    setLoadingFiltered(true);
    try {
      const response = await axios.get(`${API}/online-sales`, {
        params: { 
          branch: selectedBranch, 
          start_date: date,
          end_date: date,
          limit: 500  // Get all sales for that date
        }
      });
      setFilteredSales(response.data.sales || []);
    } catch (error) {
      console.error("Error fetching filtered sales:", error);
      setFilteredSales([]);
    } finally {
      setLoadingFiltered(false);
    }
  };

  // Delete all sales for a specific date
  const handleDeleteAll = async () => {
    if (filteredSales.length === 0) {
      toast.error("Walang sales na ide-delete");
      return;
    }
    
    setDeletingAll(true);
    const token = localStorage.getItem("auth_token");
    let successCount = 0;
    let failCount = 0;
    
    const progressToast = toast.loading(`Deleting 0/${filteredSales.length} orders...`);
    
    try {
      for (let i = 0; i < filteredSales.length; i++) {
        const sale = filteredSales[i];
        
        if (i % 5 === 0 || i === filteredSales.length - 1) {
          toast.loading(`Deleting ${i + 1}/${filteredSales.length} orders...`, { id: progressToast });
        }
        
        try {
          await axios.delete(`${API}/online-sales/${sale.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to delete ${sale.id}:`, err);
          failCount++;
        }
        
        // Small delay to prevent overwhelming server
        if (i > 0 && i % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      toast.dismiss(progressToast);
      
      if (failCount === 0) {
        toast.success(`✅ Deleted ${successCount} orders successfully!`);
      } else {
        toast.warning(`Deleted ${successCount} orders, ${failCount} failed`);
      }
      
      // Refresh data
      setShowDeleteAllConfirm(false);
      setFilteredSales([]);
      await fetchRecentSales();
      await fetchStats();
      // Also refresh records view if open
      if (showRecordsModal) {
        await fetchAllRecords();
      }
      
    } catch (error) {
      toast.dismiss(progressToast);
      toast.error("Delete failed: " + error.message);
    } finally {
      setDeletingAll(false);
    }
  };

  // Fetch all records with date range (for records view)
  const fetchAllRecords = async (startDate = null, endDate = null, loadAll = false) => {
    setLoadingRecords(true);
    try {
      const params = { 
        branch: selectedBranch, 
        limit: 1000
      };
      
      // If loading all, don't add date filters
      if (!loadAll) {
        const start = startDate || recordsStartDate;
        const end = endDate || recordsEndDate;
        if (start) params.start_date = start;
        if (end) params.end_date = end;
      }
      
      console.log("[Records] Fetching with params:", params);
      
      const response = await axios.get(`${API}/online-sales`, { params });
      const records = response.data.sales || [];
      
      console.log(`[Records] Got ${records.length} records`);
      
      setAllRecords(records);
      
      // Calculate stats
      const totalQty = records.reduce((sum, r) => sum + (r.quantity || 0), 0);
      setRecordsStats({
        total: records.length,
        totalQty: totalQty
      });
    } catch (error) {
      console.error("Error fetching records:", error);
      setAllRecords([]);
      setRecordsStats({ total: 0, totalQty: 0 });
    } finally {
      setLoadingRecords(false);
    }
  };

  // Open records modal - load all records first
  const openRecordsModal = () => {
    setShowRecordsModal(true);
    fetchAllRecords(null, null, true); // Load ALL records initially (no date filter)
  };

  // Export sales records to CSV
  const exportSalesRecords = () => {
    if (allRecords.length === 0) {
      toast.error("Walang records na i-export");
      return;
    }
    
    // CSV header matching typical online sales format
    const headers = [
      "Encoder",
      "Date", 
      "Channel",
      "Tracking Number",
      "Item Code",
      "Item Name",
      "Quantity",
      "Customer Name",
      "Address",
      "Notes"
    ];
    
    // Convert records to CSV rows
    const rows = allRecords.map(record => [
      record.encoder || "",
      record.sale_date || "",
      record.channel || "",
      record.tracking_number || "",
      record.item_code || "",
      record.item_name || "",
      record.quantity || 0,
      record.customer_name || "",
      record.address || "",
      record.notes || ""
    ]);
    
    // Build CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma
        const escaped = String(cell).replace(/"/g, '""');
        return escaped.includes(',') || escaped.includes('\n') ? `"${escaped}"` : escaped;
      }).join(","))
    ].join("\n");
    
    // Create download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Online-Sales_${selectedBranch}_${recordsStartDate || 'all'}_to_${recordsEndDate || 'now'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`✅ Exported ${allRecords.length} records to CSV!`);
  };

  // Check stock for an item (Display Area only)
  const checkStock = async (itemName) => {
    if (!itemName || stockCache[itemName] !== undefined) return stockCache[itemName];
    
    try {
      const response = await axios.get(`${API}/inventory/stock-check/${selectedBranch}/${encodeURIComponent(itemName)}`);
      // POS uses ONLY Display Area stock - Stock Room is NOT sellable directly
      const stock = response.data.display_area_stock ?? 0;
      setStockCache(prev => ({ ...prev, [itemName]: stock }));
      return stock;
    } catch (error) {
      console.error("Error checking stock:", error);
      return 0;
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    fetchRecentSales();
    fetchStats();
    setStockCache({});
  }, [selectedBranch]);

  // Filter items based on search
  useEffect(() => {
    if (!searchQuery) {
      setFilteredItems([]);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = items.filter(item => 
        item.item_name?.toLowerCase().includes(query) ||
        item.item_code?.toLowerCase().includes(query)
      ).slice(0, 15);
      setFilteredItems(filtered);
    }
  }, [searchQuery, items]);

  // Add item to cart
  const addToCart = async (item) => {
    const existingIdx = cart.findIndex(c => c.item_name === item.item_name);
    
    if (existingIdx >= 0) {
      const newCart = [...cart];
      newCart[existingIdx].quantity += 1;
      setCart(newCart);
    } else {
      const stock = await checkStock(item.item_name);
      setCart([...cart, {
        ...item,
        quantity: 1,
        available_stock: stock
      }]);
    }
    
    setSearchQuery("");
    setFilteredItems([]);
    searchRef.current?.focus();
  };

  // Update cart item quantity
  const updateCartQuantity = (index, newQty) => {
    if (newQty <= 0) {
      removeFromCart(index);
      return;
    }
    
    const newCart = [...cart];
    newCart[index].quantity = newQty;
    setCart(newCart);
  };

  // Remove item from cart
  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
    setCustomerName("");
    setAddress("");
    setTrackingNumber("");
    setNotes("");
    setChannel("Shopee");
  };

  // Calculate cart total
  const cartTotal = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Check if cart has stock issues
  const hasStockIssues = cart.some(item => item.quantity > (item.available_stock || 0));

  // Submit order (all items in cart)
  const handleSubmit = async () => {
    if (cart.length === 0) {
      toast.error("Walang item sa cart");
      return;
    }
    
    if (!trackingNumber.trim()) {
      toast.error("Tracking number is required");
      return;
    }

    // Check stock for all items
    for (const item of cart) {
      if (item.quantity > (item.available_stock || 0)) {
        toast.error(`Hindi sapat ang stock para sa ${item.item_name}! Available: ${item.available_stock || 0}`);
        return;
      }
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/online-sales/batch`, {
        branch: selectedBranch,
        items: cart.map(item => ({
          item_id: item.id,
          item_name: item.item_name,
          item_code: item.item_code || "",
          category: item.category || "",
          quantity: item.quantity
        })),
        encoder: user?.name || user?.email?.split('@')[0] || "System",
        channel: channel,
        tracking_number: trackingNumber,
        customer_name: customerName,
        address: address,
        notes: notes,
        order_date: orderDate
      });

      // Check if there are any errors in the response (partial save)
      const errors = response.data.errors;
      const savedCount = response.data.saved_count || 0;
      const totalItems = cart.length;
      
      if (errors && errors.length > 0) {
        // Some items FAILED to save - SHOW BIG WARNING
        toast.error(
          <div className="text-left">
            <div className="font-bold text-red-700">⚠️ MAY {errors.length} ITEM(S) NA HINDI NA-SAVE!</div>
            <div className="text-sm mt-1">Na-save: {savedCount} out of {totalItems} items</div>
            <div className="text-sm mt-2 font-semibold text-red-600">Mga item na HINDI na-save:</div>
            <ul className="text-xs mt-1 space-y-1">
              {errors.slice(0, 5).map((err, i) => (
                <li key={i} className="bg-red-100 p-1 rounded">❌ {err}</li>
              ))}
              {errors.length > 5 && <li className="text-gray-500">...at {errors.length - 5} pa</li>}
            </ul>
            <div className="text-xs mt-2 text-red-700">I-check kung may stock sa Display Area!</div>
          </div>,
          { duration: 20000 }
        );
      } else {
        toast.success(`✅ ${savedCount} item(s) saved! Total: ${response.data.total_quantity} units`);
      }
      
      clearCart();
      await fetchRecentSales();
      await fetchStats();
      setStockCache({});
      
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error(error.response?.data?.detail || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const openDeleteConfirm = (sale) => {
    setSaleToDelete(sale);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!saleToDelete) return;
    
    const saleId = saleToDelete.id || saleToDelete._id;
    if (!saleId) {
      toast.error("Cannot delete: sale ID missing. Refresh and try again.");
      setShowDeleteConfirm(false);
      setSaleToDelete(null);
      return;
    }
    
    setDeleting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.delete(`${API}/online-sales/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message || "Sale deleted successfully");
      setShowDeleteConfirm(false);
      setSaleToDelete(null);
      
      try { await fetchRecentSales(); } catch (e) { console.error("Refresh sales failed:", e); }
      try { await fetchStats(); } catch (e) { console.error("Refresh stats failed:", e); }
    } catch (error) {
      console.error("Online sale delete error:", error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.response?.data?.message;
      const msg = detail
        ? `Delete failed (${status}): ${detail}`
        : status
          ? `Delete failed: HTTP ${status} — ${error.message}`
          : `Delete failed: ${error.message || 'Network error — check internet connection'}`;
      toast.error(msg, { duration: 7000 });
    } finally {
      setDeleting(false);
    }
  };

  // Handle bulk delete for a specific date
  const handleBulkDelete = async (recordsToDelete) => {
    if (!recordsToDelete || recordsToDelete.length === 0) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem("auth_token");
      let deleted = 0;
      let failed = 0;
      
      for (const record of recordsToDelete) {
        try {
          await axios.delete(`${API}/online-sales/${record.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deleted++;
        } catch (err) {
          failed++;
        }
      }
      
      toast.success(`Deleted ${deleted} records${failed > 0 ? ` (${failed} failed)` : ''}`);
      await fetchRecentSales();
      await fetchStats();
      await fetchAllRecords(recordsStartDate, recordsEndDate, false);
    } catch (error) {
      toast.error("Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // Open edit modal with record data
  const openEditModal = (record) => {
    setEditingRecord(record);
    setEditForm({
      quantity: record.quantity || 0,
      tracking_number: record.tracking_number || "",
      channel: record.channel || "Shopee",
      sale_date: record.sale_date || new Date().toISOString().split('T')[0],
      customer_name: record.customer_name || "",
      address: record.address || "",
      notes: record.notes || ""
    });
    setShowEditModal(true);
  };

  // Save edited record
  const handleEditSave = async () => {
    if (!editingRecord) return;
    
    setEditSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.put(`${API}/online-sales/${editingRecord.id}`, {
        quantity: editForm.quantity,
        tracking_number: editForm.tracking_number,
        channel: editForm.channel,
        sale_date: editForm.sale_date,
        customer_name: editForm.customer_name,
        address: editForm.address,
        notes: editForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Record updated successfully!");
      setShowEditModal(false);
      setEditingRecord(null);
      
      // Refresh data
      await fetchRecentSales();
      await fetchStats();
      if (showRecordsModal) {
        await fetchAllRecords();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  // ==================== CSV IMPORT FUNCTIONS ====================
  
  // Parse CSV file - robust handling for any row count
  const parseCSV = (text) => {
    // Normalize line endings (handle Windows \r\n, Mac \r, Unix \n)
    const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Split by newlines but keep ALL rows including empty-looking ones
    const lines = normalizedText.split('\n');
    const rows = [];
    
    console.log(`[CSV Parser] Raw lines count: ${lines.length}`);
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Skip completely empty lines only
      if (!line || line.trim() === '') {
        console.log(`[CSV Parser] Skipping empty line ${i + 1}`);
        continue;
      }
      
      // Handle both comma and tab-separated values
      let cells;
      if (line.includes('\t')) {
        cells = line.split('\t').map(c => c.trim().replace(/^"|"$/g, '')); // Remove surrounding quotes
      } else {
        // Handle CSV with potential quoted values
        cells = [];
        let current = '';
        let inQuotes = false;
        for (let j = 0; j < line.length; j++) {
          const char = line[j];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            cells.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        // Don't forget the last cell!
        cells.push(current.trim());
      }
      
      // Only add row if it has meaningful content
      if (cells.some(cell => cell && cell.length > 0)) {
        rows.push(cells);
      }
    }
    
    console.log(`[CSV Parser] Parsed rows count: ${rows.length}`);
    return rows;
  };

  // Handle file selection - robust for ANY file size
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Get file extension
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    
    // Check if it's an Excel file
    const isExcel = ['.xlsx', '.xls'].includes(fileExtension);
    
    // Validate file type
    const allowedExtensions = ['.csv', '.txt', '.tsv', '.xlsx', '.xls'];
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(`Invalid file type: ${file.name}\n\nPlease upload a CSV or Excel file (.csv, .txt, .xlsx, .xls).`);
      e.target.value = '';
      return;
    }
    
    // Show loading toast for large files
    const loadingToast = toast.loading(`Reading ${file.name}...`);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        let rows = [];
        
        if (isExcel) {
          // Parse Excel file using XLSX library
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          
          console.log(`[Excel Import] File loaded: ${file.name}, rows: ${jsonData.length}`);
          
          if (jsonData.length === 0) {
            toast.dismiss(loadingToast);
            toast.error("The Excel file appears to be empty.");
            return;
          }
          
          rows = jsonData.map(row => row.map(cell => String(cell || '').trim()));
        } else {
          // Parse CSV file
          const text = event.target.result;
          
          console.log(`[CSV Import] File loaded: ${file.name}, size: ${text.length} bytes`);
          
          // Check if file content looks like binary/non-text data
          const hasBinaryContent = /[\x00-\x08\x0E-\x1F]/.test(text) || text.includes('\ufffd');
          if (hasBinaryContent) {
            toast.dismiss(loadingToast);
            toast.error("This file appears to be in binary format, not CSV.\n\nPlease use the Excel (.xlsx) format instead or export as CSV.");
            return;
          }
          
          rows = parseCSV(text);
        }
        
        console.log(`[Import] Total rows parsed: ${rows.length}`);
        
        if (rows.length === 0) {
          toast.dismiss(loadingToast);
          toast.error("The file appears to be empty or in an unsupported format.");
          return;
        }
        
        // ROBUST Header detection: a real header row should NOT contain digits
        // (column names are usually plain text like "Tracking", "Item", "Qty" — not "12345" or "2026-01-01")
        // and should match EXACTLY (not substring) common header terms.
        let dataRows = rows;
        let hasHeader = false;
        
        if (rows.length > 0) {
          const firstRow = rows[0];
          // EXACT header term whitelist — must match cell value exactly (case-insensitive, trimmed)
          const exactHeaderTerms = new Set([
            'date', 'order date', 'sale date',
            'tracking', 'tracking #', 'tracking number', 'tracking no', 'tracking no.', 'awb', 'waybill',
            'item', 'item name', 'item code', 'product', 'product name', 'description',
            'qty', 'quantity', 'stocks', 'stock', 'pcs',
            'order', 'order id', 'order #', 'order no', 'order number',
            'platform', 'channel', 'source',
            'customer', 'customer name', 'name', 'recipient', 'consignee',
            'amount', 'price', 'total', 'cod',
            'address', 'phone', 'contact', 'remarks', 'notes',
            '#', 'no', 'no.', 'row'
          ]);
          
          const nonEmptyCells = firstRow.filter(c => c && String(c).trim());
          
          // Cells with ANY digit are very unlikely to be header labels
          const cellsWithDigits = nonEmptyCells.filter(c => /\d/.test(String(c))).length;
          
          // Cells matching the EXACT header whitelist
          const exactMatches = nonEmptyCells.filter(c =>
            exactHeaderTerms.has(String(c).toLowerCase().trim())
          ).length;
          
          // Header detection: 2+ exact whitelisted terms AND no cells with digits
          if (exactMatches >= 2 && cellsWithDigits === 0) {
            hasHeader = true;
            dataRows = rows.slice(1);
            console.log(`[Import] Header detected (${exactMatches} exact matches, 0 digit cells). Row 1 skipped.`);
          } else {
            console.log(`[Import] First row treated as DATA (${exactMatches} exact matches, ${cellsWithDigits} cells with digits). NO row dropped.`);
          }
        }
        
        console.log(`[Import] Data rows to process: ${dataRows.length}${hasHeader ? ' (header skipped)' : ''}`);
        
        // Process rows and group by tracking number
        processImportData(dataRows);
        setImportResults([]); // Clear previous results
        
        toast.dismiss(loadingToast);
        toast.success(`File loaded: ${dataRows.length} data rows found${hasHeader ? ' (header skipped)' : ''}`);
        setShowImportModal(true);
        
      } catch (error) {
        console.error("Import parse error:", error);
        toast.dismiss(loadingToast);
        toast.error(`Failed to parse file: ${error.message}\n\nMake sure the file is a valid CSV or Excel format.`);
      }
    };
    
    reader.onerror = () => {
      toast.dismiss(loadingToast);
      toast.error("Failed to read file. Please try again.");
    };
    
    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
    
    // Reset file input
    e.target.value = '';
  };

  // Process import data - group by tracking number (handles large imports)
  const processImportData = (rows) => {
    const orders = {};
    const errors = [];
    let validRows = 0;
    let skippedRows = 0;
    
    console.log(`[CSV Import] Processing ${rows.length} rows...`);
    
    // Process in batches to avoid UI freeze for large files
    const BATCH_SIZE = 500;
    const totalBatches = Math.ceil(rows.length / BATCH_SIZE);
    
    if (totalBatches > 1) {
      console.log(`[CSV Import] Large file detected. Processing in ${totalBatches} batches.`);
    }
    
    rows.forEach((row, idx) => {
      // Expected format: [empty/index, Date, Tracking, Item, Qty] or [Date, Tracking, Item, Qty]
      let date, tracking, itemName, qty;
      
      // Debug log for first few rows
      if (idx < 5) {
        console.log(`[CSV Import] Row ${idx + 1}: ${JSON.stringify(row)}`);
      }
      
      // Helper function to normalize date to YYYY-MM-DD
      const normalizeDate = (dateStr) => {
        if (!dateStr) return new Date().toISOString().split('T')[0];
        
        const cleaned = dateStr.toString().trim();
        
        // Already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
          return cleaned;
        }
        
        // Try MM/DD/YYYY or M/D/YYYY format
        const slashMatch = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (slashMatch) {
          const month = slashMatch[1].padStart(2, '0');
          const day = slashMatch[2].padStart(2, '0');
          const year = slashMatch[3];
          return `${year}-${month}-${day}`;
        }
        
        // Try DD-MM-YYYY or D-M-YYYY format
        const dashMatch = cleaned.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        if (dashMatch) {
          const day = dashMatch[1].padStart(2, '0');
          const month = dashMatch[2].padStart(2, '0');
          const year = dashMatch[3];
          return `${year}-${month}-${day}`;
        }
        
        // Try to parse with Date object
        const parsed = new Date(cleaned);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString().split('T')[0];
        }
        
        // Fallback to today
        return new Date().toISOString().split('T')[0];
      };
      
      // Try to detect column positions - be more flexible
      if (row.length >= 5) {
        // Format: [index, Date, Tracking, Item, Qty]
        date = normalizeDate(row[1]);
        tracking = row[2];
        itemName = row[3];
        qty = row[4];
      } else if (row.length >= 4) {
        // Format: [Date, Tracking, Item, Qty]
        date = normalizeDate(row[0]);
        tracking = row[1];
        itemName = row[2];
        qty = row[3];
      } else if (row.length === 3) {
        // Format: [Tracking, Item, Qty] - no date
        date = new Date().toISOString().split('T')[0];
        tracking = row[0];
        itemName = row[1];
        qty = row[2];
      } else if (row.length === 2) {
        // Format: [Item, Qty] - no tracking (will group under "NO-TRACKING")
        date = new Date().toISOString().split('T')[0];
        tracking = `NO-TRACKING-${Date.now()}`;
        itemName = row[0];
        qty = row[1];
      } else {
        errors.push({ row: idx + 1, error: "Incomplete row data (need at least 2 columns)", data: row.join(', ') });
        skippedRows++;
        return;
      }
      
      // Clean up values
      tracking = (tracking || '').toString().trim();
      itemName = (itemName || '').toString().trim();
      
      // Validate tracking number - be more lenient
      if (!tracking || tracking === '' || tracking.toLowerCase() === 'tracking' || tracking.toLowerCase() === 'tracking number') {
        errors.push({ row: idx + 1, error: "Missing or invalid tracking number", data: row.join(', ') });
        skippedRows++;
        return;
      }
      
      // Validate item name
      if (!itemName || itemName === '' || itemName.toLowerCase() === 'item' || itemName.toLowerCase() === 'product') {
        errors.push({ row: idx + 1, error: "Missing or invalid item name", data: row.join(', ') });
        skippedRows++;
        return;
      }
      
      // Parse quantity (default to 1, handle various formats)
      let quantity = 1;
      if (qty) {
        const cleanQty = qty.toString().replace(/[^0-9.]/g, ''); // Remove non-numeric chars
        quantity = parseFloat(cleanQty) || 1;
      }
      
      // Find matching item in master list - EXACT MATCH ONLY to prevent H11→H1 type errors
      const normalizedItemName = itemName.toLowerCase().trim();
      const matchedItem = items.find(item => {
        const itemLower = item.item_name.toLowerCase().trim();
        const itemCodeLower = (item.item_code || "").toLowerCase().trim();
        
        // EXACT MATCH ONLY - no partial/substring matching
        // This prevents H11 from matching H1, etc.
        return (
          itemLower === normalizedItemName ||
          itemCodeLower === normalizedItemName
        );
      });
      
      // Group by tracking number
      const trackingKey = tracking.toUpperCase();
      if (!orders[trackingKey]) {
        orders[trackingKey] = {
          tracking_number: trackingKey,
          date: date || new Date().toISOString().split('T')[0],
          items: [],
          hasErrors: false
        };
      }
      
      const itemEntry = {
        original_name: itemName,
        matched_item: matchedItem,
        quantity: quantity,
        status: matchedItem ? 'matched' : 'unmatched'
      };
      
      if (!matchedItem) {
        orders[trackingKey].hasErrors = true;
        errors.push({ 
          row: idx + 1, 
          error: `Item not found in system: "${itemName}"`, 
          data: row.join(', '),
          tracking: trackingKey
        });
      }
      
      orders[trackingKey].items.push(itemEntry);
      validRows++;
    });
    
    // Convert to array and sort by tracking number
    const orderArray = Object.values(orders).sort((a, b) => 
      a.tracking_number.localeCompare(b.tracking_number)
    );
    
    console.log(`[CSV Import] Processing complete:`);
    console.log(`  - Total rows: ${rows.length}`);
    console.log(`  - Valid rows: ${validRows}`);
    console.log(`  - Skipped rows: ${skippedRows}`);
    console.log(`  - Orders created: ${orderArray.length}`);
    console.log(`  - Orders with errors: ${orderArray.filter(o => o.hasErrors).length}`);
    
    setImportPreview(orderArray);
    setImportErrors(errors);
    setImportStats({
      totalRows: rows.length,
      validRows,
      skippedRows,
      totalOrders: orderArray.length,
      ordersWithErrors: orderArray.filter(o => o.hasErrors).length
    });
  };

  // Check if any orders have errors (for disabling import button)
  const hasImportErrors = importPreview.some(order => order.hasErrors);
  const validOrdersCount = importPreview.filter(order => !order.hasErrors).length;
  const errorOrdersCount = importPreview.filter(order => order.hasErrors).length;

  // Execute import - handles large imports with progress (NO duplicate skipping)
  const executeImport = async () => {
    if (!importPreview || importPreview.length === 0) {
      toast.error("No orders to import");
      return;
    }
    
    // Block import if ANY orders have errors - user must fix them first
    if (hasImportErrors) {
      toast.error(`Cannot import! ${errorOrdersCount} order(s) have unrecognized items. Please fix errors first (highlighted in red).`);
      return;
    }
    
    // All orders are valid at this point
    const validOrders = importPreview;
    
    setImporting(true);
    let successCount = 0;
    let failCount = 0;
    const results = [];
    
    // Create progress toast
    const progressToast = toast.loading(`Importing 0/${validOrders.length} orders...`);
    
    try {
      // Process each order with progress updates - NO duplicate skipping
      for (let i = 0; i < validOrders.length; i++) {
        const order = validOrders[i];
        
        // Update progress every 5 orders or on last order
        if (i % 5 === 0 || i === validOrders.length - 1) {
          toast.loading(`Importing ${i + 1}/${validOrders.length} orders...`, { id: progressToast });
        }
        
        try {
          // Build order items
          const orderItems = order.items
            .filter(item => item.matched_item)
            .map(item => ({
              item_id: item.matched_item.id || item.matched_item.item_name,
              item_name: item.matched_item.item_name,
              item_code: item.matched_item.item_code || '',
              category: item.matched_item.category || '',
              quantity: item.quantity
            }));
          
          // Create order via batch endpoint
          const response = await axios.post(`${API}/online-sales/batch`, {
            branch: selectedBranch,
            tracking_number: order.tracking_number,
            channel: importChannel, // Use selected channel
            customer_name: "",
            address: "",
            notes: `Auto-imported from ${importChannel} CSV`,
            order_date: importDate || order.date, // Prefer user-selected date, fallback to CSV date
            items: orderItems,
            encoder: user?.email || "System"
          });
          
          if (response.data.success) {
            successCount++;
            results.push({ tracking: order.tracking_number, status: 'success' });
          } else {
            failCount++;
            results.push({ tracking: order.tracking_number, status: 'failed', reason: response.data.error || 'Unknown error' });
          }
        } catch (err) {
          failCount++;
          // Extract detailed error message from API response
          let errorDetail = err.response?.data?.detail || err.message || 'Unknown error';
          
          // Handle case where detail is an object (Pydantic validation error)
          if (typeof errorDetail === 'object') {
            if (Array.isArray(errorDetail)) {
              // Array of validation errors
              errorDetail = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
            } else {
              // Single object error
              errorDetail = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
            }
          }
          
          results.push({ tracking: order.tracking_number, status: 'failed', reason: String(errorDetail) });
          console.error(`Import failed for ${order.tracking_number}:`, errorDetail);
        }
        
        // Small delay every 10 orders to prevent overwhelming the server
        if (i > 0 && i % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Dismiss progress toast
      toast.dismiss(progressToast);
      
      // Show results with more detail
      if (failCount > 0 && successCount === 0) {
        // All failed - show detailed error
        toast.error(`Import failed! ${failCount} orders could not be saved.\n\nCommon issues:\n- Items not in Display Area\n- Insufficient stock`);
      } else if (failCount > 0) {
        toast.warning(`Import partial: ${successCount} success, ${failCount} failed`);
      } else {
        toast.success(`✅ Import complete: ${successCount} orders created!`);
      }
      
      // Save results for display
      setImportResults(results);
      
      // Refresh data
      await fetchRecentSales();
      await fetchStats();
      
      // Close modal if all successful
      if (failCount === 0) {
        setShowImportModal(false);
        setImportPreview([]);
        setImportErrors([]);
        setImportStats(null);
        setImportResults([]);
      }
      
    } catch (error) {
      console.error("Import error:", error);
      toast.dismiss(progressToast);
      toast.error("Import failed: " + (error.message || "Unknown error"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" data-testid="online-sales-page">
      {/* VIEW ONLY Banner for users without branch */}
      {!canEdit && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">VIEW ONLY MODE - Your email does not have a branch assigned. Contact admin to enable sales recording.</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-blue-600 flex items-center justify-center">
              <Globe className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Online Sales</h1>
              <p className="text-sm text-gray-500">Cart-style para sa online orders (Shopee, TikTok, etc.)</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedBranch} onValueChange={setSelectedBranch} disabled={!canAccessAllBranches && userBranches.length <= 1}>
              <SelectTrigger className="w-[180px]" data-testid="branch-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BRANCHES).map(([code, name]) => (
                  <SelectItem 
                    key={code} 
                    value={code}
                    disabled={!canAccessAllBranches && !userBranches.includes(code)}
                  >
                    {code} - {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!canAccessAllBranches && userBranches.length > 0 && (
              <span className="text-xs text-orange-600">🔒 {userBranches.join(", ")}</span>
            )}
            
            {/* CSV Import Button */}
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,.txt,.tsv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="gap-2 border-blue-300 text-blue-600 hover:bg-blue-50"
              data-testid="import-csv-btn"
            >
              <Upload className="h-4 w-4" />
              Import CSV/Excel
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Item Search & Cart */}
        <div className="lg:col-span-2 space-y-4">
          {/* Item Search */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search & Add Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  ref={searchRef}
                  placeholder="Type item name to search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 text-lg"
                  data-testid="item-search-input"
                />
                
                {/* Autocomplete Dropdown */}
                {filteredItems.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {filteredItems.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => canEdit && addToCart(item)}
                        disabled={!canEdit}
                        className={`w-full px-4 py-3 text-left flex items-center justify-between border-b last:border-0 ${canEdit ? 'hover:bg-blue-50' : 'opacity-50 cursor-not-allowed'}`}
                      >
                        <div>
                          <div className="font-medium text-gray-900">{item.item_name}</div>
                          <div className="text-sm text-gray-500">{item.category}</div>
                        </div>
                        <Plus className={`h-5 w-5 ${canEdit ? 'text-blue-600' : 'text-gray-400'}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Shopping Cart */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-blue-600" />
                  Order Cart ({cart.length} items)
                </CardTitle>
                {cart.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-500 hover:text-red-700">
                    <Trash2 className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {cart.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Walang item sa cart</p>
                  <p className="text-sm">Search and add items above</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        item.quantity > (item.available_stock || 0) 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{item.item_name}</div>
                        <div className="text-sm text-gray-500">
                          Display Area Stock: <span className={item.available_stock > 0 ? 'text-green-600' : 'text-red-600'}>
                            {item.available_stock || 0}
                          </span>
                          {item.quantity > (item.available_stock || 0) && (
                            <span className="ml-2 text-red-600 font-medium">
                              ⚠️ Kulang ang stock!
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Quantity Controls - Support 0.5 for single bulb */}
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateCartQuantity(idx, item.quantity - 0.5)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={item.quantity}
                          onChange={(e) => updateCartQuantity(idx, parseFloat(e.target.value) || 0)}
                          className="w-20 text-center font-bold"
                        />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateCartQuantity(idx, item.quantity + 0.5)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => removeFromCart(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Cart Summary */}
                  <div className="pt-3 border-t">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>Total Items:</span>
                      <span className="text-blue-600">{cartTotal}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Details & Submit */}
          {cart.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Channel *</label>
                    <Select value={channel} onValueChange={setChannel}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CHANNELS.map(ch => (
                          <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Tracking Number *</label>
                    <Input
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="Required"
                      className={!trackingNumber ? 'border-orange-300' : ''}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Customer Name</label>
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Order Date</label>
                    <Input
                      type="date"
                      value={orderDate}
                      onChange={(e) => setOrderDate(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Delivery Address</label>
                  <Input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                
                <Button
                  onClick={handleSubmit}
                  disabled={saving || cart.length === 0 || hasStockIssues || !trackingNumber}
                  className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <RotateCcw className="h-5 w-5 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Check className="h-5 w-5" />
                      Save Order ({cart.length} items, {cartTotal} units)
                    </span>
                  )}
                </Button>
                
                {hasStockIssues && (
                  <div className="flex items-center gap-2 text-red-600 text-sm justify-center">
                    <AlertCircle className="h-4 w-4" />
                    May item na kulang ang stock sa Display Area
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Stats & Recent Orders */}
        <div className="space-y-4">
          {/* Today's Stats */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                Today&apos;s Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats?.today_count || 0}
                  </div>
                  <div className="text-sm text-gray-500">Orders</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {stats?.today_units || 0}
                  </div>
                  <div className="text-sm text-gray-500">Units</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* View All Records Button */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="py-4">
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={openRecordsModal}
              >
                <List className="h-4 w-4 mr-2" />
                View All Records by Date
              </Button>
            </CardContent>
          </Card>

          {/* Recent Orders */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Recent Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {recentSales.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No recent orders
                  </div>
                ) : (
                  recentSales.map((sale, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 group"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 text-sm">{sale.item_name}</div>
                          <div className="text-xs text-gray-500">
                            Qty: {sale.quantity} • {sale.channel} • {sale.sale_date || 'No date'}
                          </div>
                          <div className="text-xs text-blue-600">{sale.tracking_number}</div>
                        </div>
                        {isAdmin() && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDeleteConfirm(sale)}
                            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 h-7"
                            disabled={!canEdit}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Order
            </DialogTitle>
          </DialogHeader>
          {saleToDelete && (
            <div className="py-4">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete this order? Stock will be returned to Display Area.
              </p>
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="font-medium">{saleToDelete.item_name}</div>
                <div className="text-sm text-gray-500">
                  Qty: {saleToDelete.quantity} • {saleToDelete.tracking_number}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? "Deleting..." : "Delete Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={showDeleteAllConfirm} onOpenChange={setShowDeleteAllConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete All Orders for {filterDate}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{filteredSales.length}</div>
                <div className="text-red-700">orders will be deleted</div>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              ⚠️ This will delete ALL {filteredSales.length} orders for <strong>{filterDate}</strong> and return the stock to Display Area.
            </p>
            <p className="text-red-600 text-sm mt-2 font-medium">
              This action cannot be undone!
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteAllConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAll}
              disabled={deletingAll}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingAll ? (
                <span className="flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                `Delete All ${filteredSales.length} Orders`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Records Modal */}
      <Dialog open={showRecordsModal} onOpenChange={setShowRecordsModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5 text-blue-600" />
              Online Sales Records - {selectedBranch}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Date Range Filter & View Toggle */}
            <div className="flex flex-wrap items-center gap-2 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">From:</span>
              </div>
              <Input
                type="date"
                value={recordsStartDate}
                onChange={(e) => setRecordsStartDate(e.target.value)}
                className="w-36"
              />
              <span className="text-sm font-medium text-blue-700">To:</span>
              <Input
                type="date"
                value={recordsEndDate}
                onChange={(e) => setRecordsEndDate(e.target.value)}
                className="w-36"
              />
              <Button
                onClick={() => fetchAllRecords(recordsStartDate, recordsEndDate, false)}
                disabled={loadingRecords}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loadingRecords ? <RotateCcw className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
                <span className="ml-1">Filter</span>
              </Button>
              <Button
                onClick={() => fetchAllRecords(null, null, true)}
                disabled={loadingRecords}
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                <List className="h-4 w-4 mr-1" />
                All
              </Button>
              
              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 p-1 bg-white rounded-lg border">
                <Button
                  size="sm"
                  variant={recordsViewMode === 'channel' ? 'default' : 'ghost'}
                  onClick={() => setRecordsViewMode('channel')}
                  className={`h-7 px-2 ${recordsViewMode === 'channel' ? 'bg-blue-600' : ''}`}
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant={recordsViewMode === 'table' ? 'default' : 'ghost'}
                  onClick={() => setRecordsViewMode('table')}
                  className={`h-7 px-2 ${recordsViewMode === 'table' ? 'bg-blue-600' : ''}`}
                >
                  <Table2 className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Stats */}
              <div className="ml-auto flex items-center gap-2">
                <div className="text-center px-3 py-1 bg-white rounded-lg border shadow-sm">
                  <div className="text-lg font-bold text-blue-600">{recordsStats.total}</div>
                  <div className="text-xs text-gray-500">Records</div>
                </div>
                <div className="text-center px-3 py-1 bg-white rounded-lg border shadow-sm">
                  <div className="text-lg font-bold text-green-600">{recordsStats.totalQty}</div>
                  <div className="text-xs text-gray-500">Total Qty</div>
                </div>
                {/* Export Button */}
                <Button
                  onClick={() => exportSalesRecords()}
                  disabled={allRecords.length === 0}
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="export-online-sales-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export CSV
                </Button>
              </div>
            </div>
            
            {/* Bulk Selection Toolbar — appears only when rows are selected */}
            {selectedIds.size > 0 && canEdit && (
              <div className="mb-3 p-3 bg-red-50 border-2 border-red-300 rounded-lg flex items-center justify-between" data-testid="online-bulk-toolbar">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-red-800">
                    {selectedIds.size} record{selectedIds.size > 1 ? 's' : ''} selected
                  </span>
                  <span className="text-xs text-red-600">
                    (Lahat ng stock ay BABALIK sa Display Area kapag dinelete)
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearSelection}
                    className="border-gray-400"
                    data-testid="online-bulk-clear-btn"
                  >
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleBulkDeleteSelected}
                    disabled={bulkDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="online-bulk-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {bulkDeleting ? "Deleting..." : `Delete ${selectedIds.size} Selected`}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Records View */}
            <div className="flex-1 overflow-auto">
              {loadingRecords ? (
                <div className="flex items-center justify-center h-32">
                  <RotateCcw className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : allRecords.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500 border rounded-lg">
                  <Package className="h-12 w-12 mb-2 opacity-50" />
                  <p>No records found</p>
                </div>
              ) : recordsViewMode === 'channel' ? (
                /* Channel-Based View - NOW GROUPED BY DATE within each channel */
                <div className="space-y-4">
                  {(() => {
                    // Group by Date first, then by Channel
                    const recordsByDateChannel = {};
                    allRecords.forEach(record => {
                      const date = record.sale_date || record.order_date || 'No Date';
                      const channel = record.channel || 'Other';
                      if (!recordsByDateChannel[date]) {
                        recordsByDateChannel[date] = {};
                      }
                      if (!recordsByDateChannel[date][channel]) {
                        recordsByDateChannel[date][channel] = { records: [], totalQty: 0 };
                      }
                      recordsByDateChannel[date][channel].records.push(record);
                      recordsByDateChannel[date][channel].totalQty += record.quantity || 0;
                    });
                    
                    // Sort dates descending (newest first)
                    const sortedDates = Object.keys(recordsByDateChannel).sort((a, b) => b.localeCompare(a));
                    
                    return sortedDates.map(date => (
                      <div key={date} className="border rounded-lg overflow-hidden">
                        {/* Date Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4" />
                            <span className="font-bold">{date}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm opacity-80">
                              {Object.values(recordsByDateChannel[date]).reduce((sum, ch) => sum + ch.records.length, 0)} records
                            </span>
                            {isAdmin() && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (window.confirm(`Delete ALL ${Object.values(recordsByDateChannel[date]).reduce((sum, ch) => sum + ch.records.length, 0)} records for ${date}?`)) {
                                    handleBulkDelete(Object.values(recordsByDateChannel[date]).flatMap(ch => ch.records));
                                  }
                                }}
                                className="text-white hover:bg-white/20 h-7 px-2"
                                disabled={!canEdit}
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Delete Date
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Channels for this date */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 p-2 bg-gray-50">
                          {Object.entries(recordsByDateChannel[date]).map(([channel, data]) => {
                            const config = CHANNEL_CONFIG[channel] || CHANNEL_CONFIG['Other'];
                            if (data.records.length === 0) return null;
                            
                            return (
                              <div key={channel} className={`border rounded-lg overflow-hidden ${config.borderColor}`}>
                                {/* Channel Header */}
                                <div className={`${config.color} ${config.textColor} px-3 py-1.5 flex items-center justify-between`}>
                                  <span className="font-bold text-xs">{channel}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs opacity-80">{data.records.length}</span>
                                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs font-bold">
                                      {data.totalQty}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Items List */}
                                <div className={`${config.bgLight} max-h-40 overflow-y-auto`}>
                                  {data.records.map((record, idx) => (
                                    <div 
                                      key={idx} 
                                      className={`px-2 py-1.5 border-b border-white/50 hover:bg-white/50 flex items-center justify-between group ${selectedIds.has(record.id) ? 'bg-red-100' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedIds.has(record.id)}
                                        onChange={() => toggleSelectId(record.id)}
                                        disabled={!canEdit}
                                        className="h-3.5 w-3.5 mr-2 cursor-pointer accent-red-600 flex-shrink-0"
                                        title="Select for bulk delete"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 text-xs truncate">{record.item_name}</div>
                                        <div className="text-xs text-gray-500 truncate">{record.tracking_number}</div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <span className="font-bold text-xs text-gray-800">{record.quantity}</span>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => openEditModal(record)}
                                          className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-700 h-5 w-5 p-0"
                                          disabled={!canEdit}
                                        >
                                          <Pencil className="h-3 w-3" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => {
                                            setSaleToDelete(record);
                                            setShowDeleteConfirm(true);
                                          }}
                                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 h-5 w-5 p-0"
                                          disabled={!canEdit}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              ) : (
                /* Table View - Grouped by Date */
                <div className="border rounded-lg overflow-hidden">
                  {(() => {
                    // Group records by date
                    const groupedByDate = allRecords.reduce((groups, record) => {
                      const date = record.sale_date || 'No Date';
                      if (!groups[date]) {
                        groups[date] = [];
                      }
                      groups[date].push(record);
                      return groups;
                    }, {});
                    
                    // Sort dates in descending order (newest first)
                    const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
                      if (a === 'No Date') return 1;
                      if (b === 'No Date') return -1;
                      return b.localeCompare(a);
                    });
                    
                    return (
                      <div className="divide-y">
                        {sortedDates.map(date => (
                          <div key={date} className="bg-white">
                            {/* Date Header */}
                            <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 border-b-2 border-blue-200 z-10">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={groupedByDate[date].length > 0 && groupedByDate[date].every(r => selectedIds.has(r.id))}
                                    onChange={() => toggleSelectGroup(groupedByDate[date])}
                                    className="h-4 w-4 cursor-pointer accent-blue-600"
                                    title="Select all for this date"
                                    data-testid={`select-all-date-${date}`}
                                  />
                                  <CalendarDays className="h-4 w-4 text-blue-600" />
                                  <span className="font-bold text-blue-800">{date}</span>
                                </div>
                                <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                                  {groupedByDate[date].length} records
                                </span>
                              </div>
                            </div>
                            
                            {/* Records for this date */}
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr className="border-b">
                                  <th className="px-3 py-2 text-center font-medium text-gray-700 w-10"><span className="sr-only">Select</span></th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-700">Item Name</th>
                                  <th className="px-3 py-2 text-center font-medium text-gray-700">Qty</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-700">Channel</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-700">Tracking #</th>
                                  <th className="px-3 py-2 text-center font-medium text-gray-700">Action</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {groupedByDate[date].map((record, idx) => {
                                  const config = CHANNEL_CONFIG[record.channel] || CHANNEL_CONFIG['Other'];
                                  return (
                                    <tr key={idx} className={`hover:bg-gray-50 ${selectedIds.has(record.id) ? 'bg-red-50' : ''}`}>
                                      <td className="px-3 py-2 text-center">
                                        <input
                                          type="checkbox"
                                          checked={selectedIds.has(record.id)}
                                          onChange={() => toggleSelectId(record.id)}
                                          disabled={!canEdit}
                                          className="h-4 w-4 cursor-pointer accent-red-600"
                                          data-testid={`select-row-${record.id}`}
                                        />
                                      </td>
                                      <td className="px-3 py-2">
                                        <div className="font-medium text-gray-900">{record.item_name}</div>
                                        <div className="text-xs text-gray-500">{record.item_code}</div>
                                      </td>
                                      <td className="px-3 py-2 text-center font-bold text-blue-600">{record.quantity}</td>
                                      <td className="px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color} ${config.textColor}`}>
                                          {record.channel}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 font-mono text-xs text-gray-600">{record.tracking_number}</td>
                                      <td className="px-3 py-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => openEditModal(record)}
                                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-7"
                                            disabled={!canEdit}
                                            title="Edit"
                                          >
                                            <Pencil className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => {
                                              setSaleToDelete(record);
                                              setShowDeleteConfirm(true);
                                            }}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7"
                                            disabled={!canEdit}
                                            title="Delete"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            
            {/* Delete All Button - Admin only */}
            {allRecords.length > 0 && canEdit && isAdmin() && (
              <div className="pt-4 border-t mt-4">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => {
                    setFilteredSales(allRecords);
                    setFilterDate(`${recordsStartDate} to ${recordsEndDate}`);
                    setShowDeleteAllConfirm(true);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All {allRecords.length} Records
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-blue-600" />
              Import Online Sales from CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-4 space-y-4">
            {/* Channel Selector */}
            <div className="flex items-center gap-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border">
              <div className="text-sm font-medium text-gray-700">Platform/Channel:</div>
              <div className="flex flex-wrap gap-2">
                {["Shopee", "TikTok", "Lazada", "J&T", "LBC", "Flash Express", "Other"].map(ch => (
                  <button
                    key={ch}
                    onClick={() => setImportChannel(ch)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      importChannel === ch 
                        ? ch === "Shopee" ? "bg-orange-500 text-white shadow-md" 
                        : ch === "TikTok" ? "bg-black text-white shadow-md"
                        : ch === "Lazada" ? "bg-blue-600 text-white shadow-md"
                        : ch === "J&T" ? "bg-red-500 text-white shadow-md"
                        : ch === "LBC" ? "bg-yellow-500 text-white shadow-md"
                        : ch === "Flash Express" ? "bg-green-500 text-white shadow-md"
                        : "bg-gray-600 text-white shadow-md"
                        : "bg-white text-gray-600 border hover:bg-gray-50"
                    }`}
                  >
                    {ch}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Date Picker for Import */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-amber-700">📅 Sale/Order Date</div>
                  <div className="text-xs text-amber-600">Anong date yung mga orders na ito?</div>
                </div>
                <Input
                  type="date"
                  value={importDate}
                  onChange={(e) => setImportDate(e.target.value)}
                  className="w-40 border-amber-300"
                  data-testid="import-date-picker"
                />
              </div>
            </div>
            
            {/* Import Stats */}
            {importStats && (
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-700">{importStats.totalRows}</div>
                  <div className="text-xs text-gray-500">Total Rows</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{importStats.totalOrders}</div>
                  <div className="text-xs text-green-600">Orders Detected</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{importStats.totalOrders - importStats.ordersWithErrors}</div>
                  <div className="text-xs text-blue-600">Ready to Import</div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{importStats.ordersWithErrors}</div>
                  <div className="text-xs text-red-600">With Errors</div>
                </div>
              </div>
            )}
            
            {/* Errors Section - Enhanced with clear details */}
            {importErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700 font-bold mb-3">
                  <AlertTriangle className="h-5 w-5" />
                  ⚠️ {importErrors.length} Error(s) Found - Import Blocked
                </div>
                <p className="text-sm text-red-600 mb-3">
                  Hindi ma-import dahil may items na hindi nakita sa system. I-fix muna ang errors bago mag-import:
                </p>
                <div className="max-h-48 overflow-y-auto space-y-2 bg-white rounded border border-red-200 p-2">
                  {importErrors.map((err, idx) => (
                    <div key={idx} className="text-sm border-b border-red-100 pb-2 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded">Row {err.row}</span>
                        <span className="font-mono text-xs text-gray-500">{err.tracking}</span>
                      </div>
                      <div className="text-red-700 mt-1 font-medium">{err.error}</div>
                      <div className="text-xs text-gray-500 mt-0.5">CSV Data: {err.data}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                  <p className="text-sm text-amber-800 font-medium">💡 Paano i-fix:</p>
                  <ul className="text-xs text-amber-700 mt-1 list-disc list-inside space-y-1">
                    <li>Check kung tama ang spelling ng item name sa CSV</li>
                    <li>Make sure na existing ang item sa Item Master</li>
                    <li>I-edit ang CSV file at i-upload ulit</li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* Orders Preview */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Orders to Import (grouped by Tracking #)</div>
              <div className="max-h-[40vh] overflow-y-auto border rounded-lg">
                {importPreview.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No orders to preview</p>
                    <p className="text-xs">Upload a CSV file to see preview</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Tracking Number</th>
                        <th className="px-3 py-2 text-left">Items</th>
                        <th className="px-3 py-2 text-center">Total Qty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {importPreview.map((order, idx) => (
                        <tr key={idx} className={order.hasErrors ? 'bg-red-50' : 'hover:bg-gray-50'}>
                          <td className="px-3 py-2">
                            {order.hasErrors ? (
                              <div className="flex flex-col items-center">
                                <XCircle className="h-5 w-5 text-red-500" />
                                <span className="text-xs text-red-600 font-medium">ERROR</span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center">
                                <CheckCircle className="h-5 w-5 text-green-500" />
                                <span className="text-xs text-green-600">OK</span>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{order.tracking_number}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="space-y-1">
                              {order.items.map((item, iIdx) => (
                                <div key={iIdx} className="flex items-center gap-2">
                                  {item.matched_item ? (
                                    <>
                                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                                      <span className="text-green-700">{item.matched_item.item_name}</span>
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
                                      <span className="text-red-600">
                                        <span className="line-through">{item.original_name}</span>
                                        <span className="ml-1 text-xs bg-red-100 px-1 rounded">NOT FOUND</span>
                                      </span>
                                    </>
                                  )}
                                  <span className="text-gray-500 ml-auto">×{item.quantity}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center font-bold">
                            {order.items.reduce((sum, i) => sum + i.quantity, 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            
            {/* Import Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <div className="font-medium text-blue-700 mb-1">Import Settings</div>
              <div className="text-blue-600 space-y-1">
                <div>• Branch: <strong>{selectedBranch} - {BRANCHES[selectedBranch]}</strong></div>
                <div>• Channel: <strong className={
                  importChannel === "Shopee" ? "text-orange-600" :
                  importChannel === "TikTok" ? "text-black" :
                  importChannel === "Lazada" ? "text-blue-700" :
                  importChannel === "J&T" ? "text-red-600" :
                  "text-gray-700"
                }>{importChannel}</strong></div>
                <div>• Sale Date: <strong className="text-amber-700">{importDate}</strong></div>
                <div>• Stock will be deducted from <strong>Display Area</strong></div>
              </div>
            </div>
            
            {/* Import Results - shows after import attempt */}
            {importResults.length > 0 && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                <div className="font-bold text-red-800 mb-3 flex items-center gap-2 text-base">
                  <AlertTriangle className="h-5 w-5" />
                  ⚠️ Import Errors - {importResults.filter(r => r.status === 'failed').length} Orders Failed
                </div>
                <p className="text-sm text-red-600 mb-3">
                  Ang mga order na ito ay HINDI na-save. I-check at i-fix ang mga error bago mag-import ulit:
                </p>
                <div className="max-h-48 overflow-y-auto space-y-2 bg-white rounded border border-red-200 p-2">
                  {importResults.filter(r => r.status === 'failed').map((result, idx) => (
                    <div key={idx} className="text-sm border-b border-red-100 pb-2 last:border-0">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                        <span className="font-mono bg-red-100 px-2 py-0.5 rounded text-xs">{String(result.tracking || '')}</span>
                      </div>
                      <div className="text-red-700 mt-1 ml-6 font-medium">
                        {typeof result.reason === 'object' ? JSON.stringify(result.reason) : String(result.reason || 'Unknown error')}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-200">
                  <p className="text-sm text-amber-800 font-medium">💡 Common Causes & Solutions:</p>
                  <ul className="text-xs text-amber-700 mt-2 list-disc list-inside space-y-1">
                    <li><strong>"No stock in Display Area"</strong> - Item exists but not in Display. Use "Pull Out" from Stock Room first.</li>
                    <li><strong>"Insufficient stock"</strong> - Not enough units in Display Area. Receive more stock or reduce quantity.</li>
                    <li><strong>"Item not found"</strong> - Item name doesn't match Item Master. Check spelling.</li>
                    <li><strong>"500 Error"</strong> - Server issue. Try again or contact support.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-4 w-4 mr-1" />
              Upload Different File
            </Button>
            <Button 
              onClick={executeImport}
              disabled={importing || importPreview.length === 0 || hasImportErrors}
              className={`${hasImportErrors ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {importing ? (
                <span className="flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 animate-spin" />
                  Importing...
                </span>
              ) : hasImportErrors ? (
                <span className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Fix {errorOrdersCount} Error(s) First
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Import {validOrdersCount} Orders
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Record Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Edit Online Sale Record
            </DialogTitle>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-4">
              {/* Item Info (Read-only) */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-semibold text-gray-900">{editingRecord.item_name}</div>
                <div className="text-sm text-gray-500">{editingRecord.item_code}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Quantity */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Quantity</label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editForm.quantity}
                    onChange={(e) => setEditForm(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                    className="text-lg font-bold"
                  />
                </div>

                {/* Channel */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Channel</label>
                  <Select value={editForm.channel} onValueChange={(v) => setEditForm(prev => ({ ...prev, channel: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["Shopee", "TikTok", "Lazada", "J&T", "LBC", "Flash Express", "Other"].map(ch => (
                        <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tracking Number */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Tracking Number</label>
                <Input
                  value={editForm.tracking_number}
                  onChange={(e) => setEditForm(prev => ({ ...prev, tracking_number: e.target.value }))}
                  placeholder="Tracking / Waybill number"
                />
              </div>

              {/* Sale Date */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Sale Date</label>
                <Input
                  type="date"
                  value={editForm.sale_date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, sale_date: e.target.value }))}
                />
              </div>

              {/* Customer Name */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Customer Name</label>
                <Input
                  value={editForm.customer_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="Customer name (optional)"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Notes</label>
                <Input
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button 
              onClick={handleEditSave}
              disabled={editSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editSaving ? <RotateCcw className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
